import React, { useState, useEffect, useRef } from 'react';
import './StoryViewer.css';
import { trackStoryView, trackStoryCompletion } from '../../utils/analytics';

const StoryViewer = ({ stories, initialStoryId, onClose }) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const progressInterval = useRef(null);
  const storyDuration = 5000; // 5 seconds per story item
  const progressStep = 100 / storyDuration * 10; // Update every 10ms
  
  // Get current time for scheduled publishing check
  const now = new Date();

  // Filter stories to only include those with items, not disabled, published, and within scheduled window if applicable
  const validStories = stories.filter(story => {
    // Basic filters
    if (story.disabled || story.items.length === 0 || !story.published) {
      return false;
    }

    // Check if the story has scheduled publish times
    if (story.publishStartTime && story.publishEndTime) {
      try {
        // Parse the ISO strings directly - this will correctly handle the timezone
        const startTime = new Date(story.publishStartTime);
        const endTime = new Date(story.publishEndTime);
        
        // Only include if current time is within the publish window
        return now >= startTime && now <= endTime;
      } catch (error) {
        console.error("Error checking schedule window in viewer:", error);
        return false;
      }
    }

    // Include regular published stories
    return true;
  });
  
  // Find the index of the initial story
  useEffect(() => {
    if (initialStoryId && validStories && validStories.length > 0) {
      const index = validStories.findIndex(story => story.id === initialStoryId);
      if (index !== -1) {
        setCurrentStoryIndex(index);
      }
    }
    setIsLoading(false);
  }, [initialStoryId, validStories]);
  
  // Track story view when a story is displayed - using global tracking utility
  useEffect(() => {
    if (!isLoading && validStories && validStories.length > 0) {
      const currentStory = validStories[currentStoryIndex];
      if (currentStory && currentStory.id) {
        // Track the view using the utility function
        trackStoryView(currentStory.id);
      }
    }
  }, [currentStoryIndex, isLoading, validStories]);
  
  // Handle progress animation
  useEffect(() => {
    if (isLoading || isPaused) return;
    
    // Clear any existing interval
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    
    // Reset progress
    setProgress(0);
    
    // Start new progress interval
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          // Call goToNextItem which already has setTimeout
          goToNextItem();
          return 0;
        }
        return prev + progressStep;
      });
    }, 10);
    
    progressInterval.current = interval;
    
    // Cleanup interval on unmount
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoryIndex, currentItemIndex, isPaused, isLoading]);
  
  // Handle media loaded event
  const handleMediaLoaded = () => {
    setIsLoading(false);
  };
  
  // Go to next story item
  const goToNextItem = () => {
    // Clear any existing interval to prevent conflicts
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    
    // Use setTimeout to ensure state updates happen after the current execution
    setTimeout(() => {
      const currentStory = validStories[currentStoryIndex];
      
      if (currentItemIndex < currentStory.items.length - 1) {
        // Go to next item in current story
        setCurrentItemIndex(currentItemIndex + 1);
      } else if (currentStoryIndex < validStories.length - 1) {
        // Go to first item of next story
        setCurrentStoryIndex(currentStoryIndex + 1);
        setCurrentItemIndex(0);
      } else {
        // End of all stories - track completion for the last story
        if (validStories && validStories.length > 0) {
          const lastStory = validStories[currentStoryIndex];
          if (lastStory && lastStory.id) {
            // Track the completion using the utility function
            trackStoryCompletion(lastStory.id);
          }
        }
        onClose();
      }
    }, 10);
  };
  
  // Go to previous story item
  const goToPrevItem = () => {
    // Clear any existing interval to prevent conflicts
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    
    // Use setTimeout to ensure state updates happen after the current execution
    setTimeout(() => {
      if (currentItemIndex > 0) {
        // Go to previous item in current story
        setCurrentItemIndex(currentItemIndex - 1);
      } else if (currentStoryIndex > 0) {
        // Go to last item of previous story
        const prevStory = validStories[currentStoryIndex - 1];
        setCurrentStoryIndex(currentStoryIndex - 1);
        setCurrentItemIndex(prevStory.items.length - 1);
      }
    }, 10);
  };
  
  // Handle click on left/right side of the viewer
  const handleViewerClick = (e) => {
    // Check if the click was on a link button (has story-viewer-link-button class or is a child of it)
    let target = e.target;
    while (target !== e.currentTarget) {
      if (target.classList && target.classList.contains('story-viewer-link-button')) {
        // If clicked on a link, don't navigate
        return;
      }
      target = target.parentElement;
    }
    
    const { clientX } = e;
    const { innerWidth } = window;
    
    if (clientX < innerWidth / 3) {
      // Click on left third of screen
      goToPrevItem();
    } else if (clientX > (innerWidth * 2) / 3) {
      // Click on right third of screen
      goToNextItem();
    }
  };
  
  // Handle touch to pause/resume
  const handleTouchStart = () => {
    setIsPaused(true);
  };
  
  const handleTouchEnd = () => {
    setIsPaused(false);
  };
  
  if (!validStories || validStories.length === 0 || isLoading) {
    return (
      <div className="story-viewer-container">
        <div className="story-viewer-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  
  const currentStory = validStories[currentStoryIndex];
  const currentItem = currentStory.items[currentItemIndex];
  
  if (!currentStory || !currentItem) {
    onClose();
    return null;
  }
  
  return (
    <div
      className="story-viewer-container"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <div
        className="story-viewer-close"
        onClick={onClose}
      >
        &times;
      </div>
      
      {/* Story header */}
      <div className="story-viewer-header">
        <div className="story-viewer-progress-container">
          {currentStory.items.map((item, index) => (
            <div 
              key={item.id} 
              className="story-viewer-progress-bar-container"
            >
              <div 
                className={`story-viewer-progress-bar ${index < currentItemIndex ? 'completed' : ''} ${index === currentItemIndex ? 'active' : ''}`}
                style={index === currentItemIndex ? { width: `${progress}%` } : {}}
              ></div>
            </div>
          ))}
        </div>
        
        <div className="story-viewer-user-info">
          <img 
            src={currentStory.circularImageUrl} 
            alt={currentStory.text || 'Story'} 
            className="story-viewer-user-image"
          />
          <div className="story-viewer-user-name">
            {currentStory.text || 'Story'}
          </div>
          <div className="story-viewer-timestamp">
            {/*new Date(currentItem.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })*/}
          </div>
        </div>
      </div>
      
      {/* Story content */}
      <div className="story-viewer-content" onClick={handleViewerClick}>
        <div className="story-viewer-media-container">
          {currentItem.mediaType === 'PHOTO' ? (
            <img
              src={currentItem.mediaUrl}
              alt="Story"
              className="story-viewer-media"
              onLoad={handleMediaLoaded}
            />
          ) : (
            <video
              src={currentItem.mediaUrl}
              className="story-viewer-media"
              autoPlay
              muted
              playsInline
              onLoadedData={handleMediaLoaded}
            />
          )}
          
          {/* Link button if the item has a link */}
          {currentItem.linkUrl && (
            <a
              href={currentItem.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="story-viewer-link-button"
              onClick={(e) => e.stopPropagation()}
              style={{
                left: `${(currentItem.linkPositionX || 0.5) * 100}%`,
                top: `${(currentItem.linkPositionY || 0.5) * 100}%`
              }}
            >
              <span className="link-icon">
                <i className="bi bi-link-45deg"></i>
              </span>
              <span className="link-text">
                {currentItem.linkLabel || 'Open Link'}
              </span>
            </a>
          )}
        </div>
      </div>
      
      {/* Navigation buttons */}
      <div
        className="story-viewer-navigation-button left-button"
        onClick={goToPrevItem}
      >
        <span className="arrow-icon">&#10094;</span>
      </div>
      <div
        className="story-viewer-navigation-button right-button"
        onClick={goToNextItem}
      >
        <span className="arrow-icon">&#10095;</span>
      </div>
    </div>
  );
};

export default StoryViewer;